import { useState, useRef } from "react";
import { createOrder } from "@/api/orders";
import { preparePayment } from "@/api/payments";
import type { Plan, Style } from "@/types/api";
import { event as gaEvent } from "@/lib/gtag";

declare global {
  interface Window {
    AUTHNICE: {
      requestPay: (params: any) => void;
    };
  }
}

interface PaymentProps {
  plans: Plan[];
  styles: Style[];
  bridePhotoCount?: number;
  groomPhotoCount?: number;
  customerInfo: {
    name: string;
    phone: string;
    email: string;
  };
  selectedPlan: string;
  selectedStyles: string[];
  uploadedS3Keys: string[];
}

export default function Payment({
  plans,
  styles,
  bridePhotoCount = 0,
  groomPhotoCount = 0,
  customerInfo,
  selectedPlan,
  selectedStyles,
  uploadedS3Keys,
}: PaymentProps) {
  const selectedPlanData = plans.find((p) => p.code === selectedPlan);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [currentStep, setCurrentStep] = useState<
    "idle" | "creating-order" | "preparing-payment" | "calling-nicepay"
  >("idle");
  const isProcessingRef = useRef(false);

  const getStyleName = (key: string) =>
    styles.find((s) => s.categoryKey === key)?.name || key;

  const handlePayment = async () => {
    if (isProcessingRef.current) return;
    gaEvent('payment_attempt', {
      plan: selectedPlan,
      amount: selectedPlanData?.price || 0,
      styles_count: selectedStyles.length,
    });
    isProcessingRef.current = true;
    setIsProcessing(true);
    setError("");

    try {
      // 1. 주문 생성
      setCurrentStep("creating-order");
      const selections = selectedStyles.map((categoryKey) => ({
        categoryKey,
      }));

      const order = await createOrder({
        planIdx: selectedPlanData!.planIdx,
        expectedAmount: selectedPlanData!.price,
        customerInfo: {
          name: customerInfo.name,
          phone: customerInfo.phone,
          email: customerInfo.email,
        },
        uploadedFileKeys: uploadedS3Keys.filter(Boolean),
        selections,
      });

      // 2. 결제 준비
      setCurrentStep("preparing-payment");
      await preparePayment(order.orderId);

      // 3. Nicepay 결제 호출
      setCurrentStep("calling-nicepay");

      if (typeof window !== "undefined" && window.AUTHNICE) {
        const apiBase = import.meta.env.VITE_PUBLIC_API_BASE_URL || "";
        const returnUrl = apiBase
          ? `${apiBase}/v1/payments/return`
          : `${window.location.origin}/v1/payments/return`;

        window.AUTHNICE.requestPay({
          clientId: import.meta.env.VITE_PUBLIC_NICEPAY_CLIENT_ID,
          method: 'kakaopay',
          orderId: order.orderId,
          amount: order.amount,
          goodsName: order.productName,
          returnUrl,
          fnError: (result: any) => {
            gaEvent('payment_error', { plan: selectedPlan, error: result?.errorMsg || 'unknown' });
            setError(result?.errorMsg || "결제 중 오류가 발생했습니다.");
            setIsProcessing(false);
            isProcessingRef.current = false;
          },
        });
      } else {
        throw new Error("결제 모듈이 로드되지 않았습니다.");
      }
    } catch (err: any) {
      console.error("결제 처리 오류:", err);
      setError(
        err?.response?.data?.message || "주문 처리 중 문제가 발생했습니다.",
      );
      setIsProcessing(false);
      isProcessingRef.current = false;
    } finally {
      setCurrentStep("idle");
    }
  };

  const stepLabels: Record<string, string> = {
    "creating-order": "주문 생성 중...",
    "preparing-payment": "결제 준비 중...",
    "calling-nicepay": "결제 창 열기...",
  };

  return (
    <div>
      <div className="text-center mb-10">
        <h2 className="font-serif text-3xl font-light text-[#2D2D2D] mb-4">
          결제하기
        </h2>
        <p className="text-gray-500 text-sm font-light leading-relaxed">
          주문 내용을 확인하고 결제를 진행해주세요.
        </p>
      </div>

      {/* Order Summary */}
      <div className="bg-white rounded-2xl p-8 border border-gray-200 mb-8">
        <h3 className="font-serif text-xl font-medium text-[#2D2D2D] mb-6">
          주문 요약
        </h3>

        <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 shrink-0">고객명</span>
            <span className="text-[#2D2D2D] font-medium text-right">
              {customerInfo.name}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 shrink-0">연락처</span>
            <span className="text-[#2D2D2D] font-medium text-right">
              {customerInfo.phone}
            </span>
          </div>
          <div className="flex justify-between text-sm gap-4">
            <span className="text-gray-600 shrink-0">이메일</span>
            <span className="text-[#2D2D2D] font-medium text-right break-all">
              {customerInfo.email}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 shrink-0">업로드 사진</span>
            <span className="text-[#2D2D2D] font-medium text-right">
              신부 {bridePhotoCount}장 · 신랑 {groomPhotoCount}장
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 shrink-0">요금제</span>
            <span className="text-[#2D2D2D] font-medium text-right">
              {selectedPlanData?.name}
            </span>
          </div>
        </div>

        {/* 선택한 스타일 */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <p className="text-sm text-gray-600 mb-3">선택 스타일</p>
          <div className="space-y-2">
            {selectedStyles.map((styleId, idx) => (
              <div
                key={styleId}
                className="flex items-center gap-2 text-sm"
              >
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#C9A96E] text-white text-xs font-bold shrink-0">
                  {idx + 1}
                </span>
                <span className="text-[#2D2D2D] font-medium">
                  {getStyleName(styleId)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between items-center mb-8">
          <span className="text-lg font-medium text-[#2D2D2D]">
            총 결제금액
          </span>
          <span className="text-3xl font-bold text-[#C9A96E]">
            {selectedPlanData?.price.toLocaleString()}원
          </span>
        </div>

        {/* 결제 수단 */}
        <div className="mb-6">
          <p className="text-sm font-medium text-[#2D2D2D] mb-3">결제 수단</p>
          <label className="flex items-center gap-3 p-4 border-2 border-[#C9A96E] rounded-xl bg-[#FDF5EA] cursor-default">
            <input
              type="radio"
              name="paymentMethod"
              value="card"
              checked
              readOnly
              className="w-4 h-4 accent-[#C9A96E]"
            />
            <i className="ri-bank-card-line text-lg text-[#C9A96E]"></i>
            <span className="text-sm font-medium text-[#2D2D2D]">신용카드</span>
          </label>
        </div>

        {/* 결제 유의사항 */}
        <div className="bg-gray-50 rounded-xl p-5 mb-6">
          <h4 className="text-sm font-medium text-[#2D2D2D] mb-3 flex items-center gap-1.5">
            <i className="ri-information-line text-[#C9A96E]"></i>
            결제 유의사항
          </h4>
          <ul className="space-y-1.5 text-xs text-gray-500 font-light leading-relaxed">
            <li className="flex items-start gap-1.5">
              <span className="text-gray-400 mt-0.5">•</span>
              <span>결제는 신용카드로만 가능하며, 모든 거래는 SSL 암호화를 통해 안전하게 처리됩니다.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-gray-400 mt-0.5">•</span>
              <span>결제 금액은 부가세(VAT)가 포함된 금액입니다.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-red-400 mt-0.5">•</span>
              <span className="text-red-500 font-normal">주문 완료 후 AI 이미지 생성이 시작되면 환불이 불가합니다.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-gray-400 mt-0.5">•</span>
              <span>서비스 제공기간은 <strong className="font-medium text-[#2D2D2D]">결제일로부터 3개월</strong>이며, 해당 기간 내에 재생성 및 다운로드가 가능합니다.</span>
            </li>
          </ul>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
            <i className="ri-error-warning-line mr-1"></i>
            {error}
          </div>
        )}

        <button
          onClick={handlePayment}
          disabled={isProcessing}
          className="w-full py-4 bg-[#C9A96E] text-white font-medium rounded-xl hover:bg-[#b8935a] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              {stepLabels[currentStep] || "처리 중..."}
            </span>
          ) : (
            "결제하기"
          )}
        </button>
      </div>

      {/* Payment Info */}
      <div className="bg-[#FDF5EA] rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#C9A96E] shrink-0 mt-0.5">
            <i className="ri-secure-payment-line text-sm text-white"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-[#2D2D2D] mb-2">
              안전한 결제
            </p>
            <p className="text-xs text-gray-600 font-light leading-relaxed">
              모든 결제는 안전하게 암호화되어 처리됩니다. 결제 후 24시간 이내에
              결과물을 받아보실 수 있으며, 만족하지 않으실 경우 전액 환불이
              가능합니다.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
